import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import LeakCard from "@/components/feature/LeakCard";
import { SITE_ORIGIN, getCategory, getLeaksByCategory } from "@/lib/leaks";
import { categories } from "@/mocks/leaks";

export default function Topic() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategory(slug) : undefined;
  const list = slug ? getLeaksByCategory(slug) : [];

  if (!category) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-folder-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这个栏目还不存在
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            你访问的主题分类可能已经调整。可以回到首页，从主题分类区进入正在更新的栏目。
          </p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const others = categories.filter((item) => item.slug !== category.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `蝌蚪爆料 · ${category.name}`,
      url: `${SITE_ORIGIN}/topic/${category.slug}`,
      description: category.desc,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: list.map((leak, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_ORIGIN}/leak/${leak.id}`,
          name: leak.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "最新爆料", item: `${SITE_ORIGIN}/feed` },
        { "@type": "ListItem", position: 3, name: category.name },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${category.name} | 蝌蚪爆料${category.short}栏目`}
        description={`蝌蚪爆料${category.name}栏目：${category.desc} 持续更新${category.name}相关的现场线索与首发内幕。`}
        keywords={`蝌蚪爆料${category.name},蝌蚪爆料,蝌蚪爆料最新,蝌蚪爆料${category.short}`}
        path={`/topic/${category.slug}`}
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-9 md:px-8 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "最新爆料", to: "/feed" },
              { label: category.name },
            ]}
          />
          <div className="mt-4 flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 md:h-14 md:w-14">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category.desc}
              </p>
              <p className="mt-3 text-xs text-foreground-500">
                当前栏目共收录 <span className="font-medium text-foreground-900">{list.length}</span> 条爆料
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((leak) => (
              <LeakCard key={leak.id} leak={leak} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-archive-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              该栏目内容正在整理中
            </p>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              编辑正在核实新的线索，先去看看其他栏目吧。
            </p>
          </div>
        )}

        <div className="mt-14 border-t border-background-200 pt-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">其他主题分类</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((item) => (
              <Link
                key={item.slug}
                to={`/topic/${item.slug}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-700">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.name}
                  </span>
                  <span className="mt-0.5 line-clamp-1 text-xs text-foreground-500">{item.desc}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}